// src/components/admin/BlogManagement.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  FolderOpen, 
  Tag as TagIcon, 
  RefreshCw,
  PlusCircle,
  Search,
  Filter,
  ChevronDown,
  ArrowLeft,
} from 'lucide-react';
import Dashboard from './blog/Dashboard';
import PostsManagement from './blog/PostsManagement';
import CategoriesManagement from './blog/CategoriesManagement';
import TagsManagement from './blog/TagsManagement';
import NotificationBar from './shared/NotificationBar';
// import { useNotification } from '../../context/NotificationContext';



const BlogManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    categories: 0,
    tags: 0,
    totalViews: 0
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Parse the path to determine active tab
    const path = location.pathname;
    if (path.includes('/posts')) {
      setActiveTab('posts');
    } else if (path.includes('/categories')) {
      setActiveTab('categories');
    } else if (path.includes('/tags')) {
      setActiveTab('tags');
    } else {
      setActiveTab('dashboard');
    }
    
    fetchStats();
  }, [location.pathname]);
  
  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/blog/stats', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch blog stats');
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching blog stats:', error);
      showNotification('error', 'Failed to load blog statistics');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    switch (tab) {
      case 'dashboard':
        navigate('/pitech/blog');
        break;
      case 'posts':
        navigate('/pitech/blog/posts');
        break;
      case 'categories':
        navigate('/pitech/blog/categories');
        break;
      case 'tags':
        navigate('/pitech/blog/tags');
        break;
      default:
        navigate('/pitech/blog');
    }
  };

  const showNotification = (type, message, duration = 5000) => {
    setNotification({ type, message });
    if (duration) {
      setTimeout(() => setNotification(null), duration);
    }
  };
  
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard stats={stats} onRefresh={fetchStats} />;
      case 'posts':
        return <PostsManagement showNotification={showNotification} />;
      case 'categories':
        return <CategoriesManagement showNotification={showNotification} />;
      case 'tags':
        return <TagsManagement showNotification={showNotification} />;
      default:
        return <Dashboard stats={stats} onRefresh={fetchStats} />;
    }
  };
  
  return (
    <div className=" container mx-auto px-4 py-6">
      {notification && (
        <NotificationBar 
          type={notification.type} 
          message={notification.message} 
          onClose={() => setNotification(null)}
        />
      )}
      
        {/* Header */}
        <header className="bg-white mb-3 border-b border-gray-200 shadow-sm sticky top-0 z-10">
         <div className="container mx-auto px-4 py-4 flex items-center justify-between">
           <div className="flex items-center">
             <button 
              onClick={() => navigate('/pitech')}
              style={{ 
                boxShadow: 'none', 
                border: 'none', 
                background: 'transparent',
                transform: 'none',
                padding: 0,
                overflow: 'visible'
              }}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-gray-800">Blog Management</h1>
          </div>
          
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ 
                boxShadow: 'none', 
                border: 'none', 
                background: 'transparent',
                transform: 'none',
                padding: 0,
                overflow: 'visible'
              }}
              className="p-2 text-gray-600 hover:text-gray-900"
            >
              <Filter size={24} />
            </button>
          </div>
          
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={() => navigate('../pitech/blog/posts/new')}
              style={{ 
                boxShadow: 'none', 
                border: 'none', 
                background: 'transparent',
                transform: 'none',
                overflow: 'visible'
              }}
              className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
            >
              <PlusCircle size={18} />
              <span>New Post</span>
            </button>
          </div>
        </div>
      </header>

      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full md:w-64 lg:w-72 shrink-0">
          <div className="bg-white rounded-lg shadow-md p-4">
            <nav className="space-y-1">
              <button
                onClick={() => handleTabChange('dashboard')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-md transition-colors ${
                  activeTab === 'dashboard'
                    ? 'bg-blue-50 font-normal text-blue-700'
                    : 'text-gray-700 font-normal hover:bg-gray-50'
                }`}
              >
                <LayoutDashboard size={20} />
                <span className="font-normal">Dashboard</span>
              </button>
              
              <button
                onClick={() => handleTabChange('posts')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-md transition-colors ${
                  activeTab === 'posts'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FileText size={20} />
                <span className="font-normal">Posts</span>
              </button>
              
              <button
                onClick={() => handleTabChange('categories')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-md transition-colors ${
                  activeTab === 'categories'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FolderOpen size={20} />
                <span className="font-normal">Categories</span>
              </button>
              
              <button
                onClick={() => handleTabChange('tags')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-md transition-colors ${
                  activeTab === 'tags'
                    ? 'bg-blue-50  text-blue-700'
                    : 'text-gray-700  hover:bg-gray-50'
                }`}
              >
                <TagIcon size={20} />
                <span className="font-normal">Tags</span>
              </button>
            </nav>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Total Posts</span>
                  <span className="font-normal">{stats.totalPosts}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Published</span>
                  <span className="font-normal">{stats.publishedPosts}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Drafts</span>
                  <span className="font-normal">{stats.draftPosts}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Categories</span>
                  <span className="font-normal">{stats.categories}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Tags</span>
                  <span className="font-normal">{stats.tags}</span>
                </div>
              </div>
              
              <button
                onClick={fetchStats}
                className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                disabled={isLoading}
              >
                <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
                <span>Refresh Stats</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1">
          {renderContent()}
        </div>
      </div>
      <style jsx="true">{`
        button {
          box-shadow: none !important;
          border: none !important;
          background: transparent !important;
          transform: none !important;
          overflow: visible !important;
        }
        
        button.bg-blue-600 {
          background-color: #2563eb !important;
        }
        button.bg-blue-600:hover {
          background-color: #1d4ed8 !important;
        }
        
        button.bg-gray-100 {
          background-color: #f3f4f6 !important;
        }
        button.bg-gray-100:hover {
          background-color: #e5e7eb !important;
        }
        
        button.bg-blue-50 {
          background-color: #eff6ff !important;
        }
        
        button.hover\\:bg-gray-50:hover {
          background-color: #f9fafb !important;
        }
       `}</style>
    </div>
  );
};

export default BlogManagement;



